import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import { checkData, deleteItem, updateData } from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import { GolfPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import {
  RETAIL_SUB_CATEGORY_EMPTY_LIST_MESSAGE,
  RETAIL_SUB_CATEGORY_FIELD_NAME,
  RETAIL_SUB_CATEGORY_LABEL_ACTIVE,
  RETAIL_SUB_CATEGORY_LABEL_NAME,
  RETAIL_SUB_CATEGORY_LABEL_NUMBER,
  RETAIL_SUB_CATEGORY_LEVELS,
  RETAIL_SUB_CATEGORY_LIST_TITLE,
  RETAIL_SUB_CATEGORY_BUTTON_SUFFIX,
  RETAIL_SUB_CATEGORY_PAGE_TITLE,
  RETAIL_SUB_CATEGORY_SAMPLE_WARNING,
  RETAIL_SUB_CATEGORY_SELECT_LEVEL_PLACEHOLDER,
  RETAIL_SUB_CATEGORY_TOOLTIPS,
  RETAIL_SUB_CATEGORY_VALIDATION_MESSAGES,
  RetailSubCategoryLevel,
  buildRetailSubCategoryPayload,
  formatRetailSubCategoriesForDnd,
  getRetailSubCategoryLevelLabel,
  getRetailSubCategoryNameFromRow,
  isRetailSubCategoryDuplicateEntry,
  isRetailSubCategoryRowActive,
  parseRetailSubCategoryLevelFromRow,
  RETAIL_SUB_CATEGORY_DND_LEVEL_LABEL,
  stripRetailSubCategoryDndDisplayFields,
} from "../../../constants/RetailSubCategoriesFields";

const RetailSubCategories = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [subCategoryNumber, setSubCategoryNumber] = useState<
    RetailSubCategoryLevel | ""
  >("");
  const [subCategoryNames, setSubCategoryNames] = useState<string[]>([""]);
  const [nameErrors, setNameErrors] = useState<boolean[]>([false]);
  const [nameErrorMessages, setNameErrorMessages] = useState<string[]>([""]);
  const [active, setActive] = useState<boolean>(true);
  const [numberError, setNumberError] = useState<boolean>(false);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  useEffect(() => {
    fieldValues && setItems(Array.isArray(fieldValues) ? fieldValues : []);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const resetNameErrors = (count: number) => {
    setNameErrors(Array(count).fill(false));
    setNameErrorMessages(Array(count).fill(""));
  };

  const updateNameAt = (index: number, value: string) => {
    setSubCategoryNames((prev) =>
      prev.map((name, i) => (i === index ? value : name)),
    );
    setNameErrors((prev) =>
      prev.map((error, i) => (i === index ? false : error)),
    );
    setNameErrorMessages((prev) =>
      prev.map((message, i) => (i === index ? "" : message)),
    );
  };

  const addNameField = () => {
    setSubCategoryNames((prev) => [...prev, ""]);
    setNameErrors((prev) => [...prev, false]);
    setNameErrorMessages((prev) => [...prev, ""]);
  };

  const removeNameField = (index: number) => {
    if (subCategoryNames.length <= 1) return;
    setSubCategoryNames((prev) => prev.filter((_, i) => i !== index));
    setNameErrors((prev) => prev.filter((_, i) => i !== index));
    setNameErrorMessages((prev) => prev.filter((_, i) => i !== index));
  };

  const validateFields = (): boolean => {
    if (subCategoryNumber === "") {
      setAlertMessage(RETAIL_SUB_CATEGORY_VALIDATION_MESSAGES.numberMandatory);
      setAlertType("error");
      setOpenAlert(true);
      setNumberError(true);
      return false;
    }
    setNumberError(false);

    const trimmedNames = subCategoryNames.map((name) => name.trim());
    const nextNameErrors = trimmedNames.map(() => false);
    const nextNameErrorMessages = trimmedNames.map(() => "");

    if (selectedId) {
      const name = trimmedNames[0] ?? "";
      if (!name) {
        nextNameErrors[0] = true;
        setNameErrors(nextNameErrors);
        setNameErrorMessages(nextNameErrorMessages);
        setAlertMessage(RETAIL_SUB_CATEGORY_VALIDATION_MESSAGES.nameMandatory);
        setAlertType("error");
        setOpenAlert(true);
        return false;
      }
      if (
        !validateString(
          name,
          validationLength.subCategoryName,
          validationType.SPACE,
        )
      ) {
        nextNameErrors[0] = true;
        nextNameErrorMessages[0] = validationMessages.subCategoryName;
        setNameErrors(nextNameErrors);
        setNameErrorMessages(nextNameErrorMessages);
        return false;
      }
      if (
        isRetailSubCategoryDuplicateEntry(
          items,
          subCategoryNumber,
          name,
          selectedId,
        )
      ) {
        setAlertMessage(RETAIL_SUB_CATEGORY_VALIDATION_MESSAGES.duplicateEntry);
        setAlertType("error");
        setOpenAlert(true);
        return false;
      }
      setNameErrors(nextNameErrors);
      setNameErrorMessages(nextNameErrorMessages);
      return true;
    }

    const filledNames = trimmedNames.filter((name) => name.length > 0);
    if (filledNames.length === 0) {
      nextNameErrors[0] = true;
      setNameErrors(nextNameErrors);
      setNameErrorMessages(nextNameErrorMessages);
      setAlertMessage(RETAIL_SUB_CATEGORY_VALIDATION_MESSAGES.nameMandatory);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    let hasFieldError = false;
    trimmedNames.forEach((name, index) => {
      if (!name) return;
      if (
        !validateString(
          name,
          validationLength.subCategoryName,
          validationType.SPACE,
        )
      ) {
        nextNameErrors[index] = true;
        nextNameErrorMessages[index] = validationMessages.subCategoryName;
        hasFieldError = true;
      }
    });
    if (hasFieldError) {
      setNameErrors(nextNameErrors);
      setNameErrorMessages(nextNameErrorMessages);
      return false;
    }

    const uniqueNames = new Set(filledNames.map((name) => name.toLowerCase()));
    if (uniqueNames.size !== filledNames.length) {
      setAlertMessage(
        RETAIL_SUB_CATEGORY_VALIDATION_MESSAGES.duplicateNamesInBatch,
      );
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    for (const name of filledNames) {
      if (
        isRetailSubCategoryDuplicateEntry(
          items,
          subCategoryNumber,
          name,
          null,
        )
      ) {
        setAlertMessage(RETAIL_SUB_CATEGORY_VALIDATION_MESSAGES.duplicateEntry);
        setAlertType("error");
        setOpenAlert(true);
        return false;
      }
    }

    setNameErrors(nextNameErrors);
    setNameErrorMessages(nextNameErrorMessages);
    return true;
  };

  const updateItem = () => {
    if (!validateFields()) {
      return false;
    }
    const payload = buildRetailSubCategoryPayload(
      subCategoryNumber,
      subCategoryNames[0]?.trim() || null,
      active,
    );
    setItems((prev) =>
      prev.map((item: any) =>
        String(item.id) === String(selectedId) ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${GolfPropertyEnum.retailSubCategories} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateFields()) {
      return false;
    }
    const namesToAdd = subCategoryNames
      .map((name) => name.trim())
      .filter((name) => name.length > 0);
    const newRows = namesToAdd.map((name) =>
      buildRetailSubCategoryPayload(subCategoryNumber, name, active),
    );
    setItems((prev) =>
      updateData(prev?.length ? [...prev, ...newRows] : [...newRows]),
    );
    setLastUpdatedRecord(newRows[newRows.length - 1] ?? null);
    editItem(null);
    setEditClicked(false);
    setAlertMessage(
      namesToAdd.length === 1
        ? `${GolfPropertyEnum.retailSubCategories} added successfully`
        : `${namesToAdd.length} ${GolfPropertyEnum.retailSubCategories} added successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const editItem = (list: any) => {
    if (!list) {
      setSelectedId(null);
      setEditClicked(false);
      setSubCategoryNumber("");
      setSubCategoryNames([""]);
      resetNameErrors(1);
      setActive(true);
      setNumberError(false);
      return;
    }

    list?.id && setSelectedId(list.id);
    setEditClicked(true);
    setSubCategoryNumber(parseRetailSubCategoryLevelFromRow(list));
    setSubCategoryNames([getRetailSubCategoryNameFromRow(list) ?? ""]);
    resetNameErrors(1);
    setActive(isRetailSubCategoryRowActive(list));
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items, id));
  };

  const dndItemData = useMemo(
    () => formatRetailSubCategoriesForDnd(items),
    [items],
  );

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            {RETAIL_SUB_CATEGORY_PAGE_TITLE}
            <Tooltip
              title={tooltipComponent(RETAIL_SUB_CATEGORY_TOOLTIPS.header)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>

        <Stack direction="column" width="100%" gap="16px">
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>{RETAIL_SUB_CATEGORY_LABEL_NUMBER}</FormLabel>
              <Tooltip
                title={tooltipComponent(RETAIL_SUB_CATEGORY_TOOLTIPS.number)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <FormControl size="small" fullWidth error={numberError}>
              <Select
                displayEmpty
                value={subCategoryNumber}
                onChange={(e) => {
                  setSubCategoryNumber(
                    e.target.value === ""
                      ? ""
                      : (Number(e.target.value) as RetailSubCategoryLevel),
                  );
                  setNumberError(false);
                }}
                renderValue={(selected) =>
                  selected
                    ? getRetailSubCategoryLevelLabel(
                        selected as RetailSubCategoryLevel,
                      )
                    : RETAIL_SUB_CATEGORY_SELECT_LEVEL_PLACEHOLDER
                }
              >
                <MenuItem value="">
                  <em>{RETAIL_SUB_CATEGORY_SELECT_LEVEL_PLACEHOLDER}</em>
                </MenuItem>
                {RETAIL_SUB_CATEGORY_LEVELS.map((level) => (
                  <MenuItem key={level} value={level}>
                    {getRetailSubCategoryLevelLabel(level)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction="column" gap="8px">
            <Stack direction="row" alignItems="center">
              <FormLabel>{RETAIL_SUB_CATEGORY_LABEL_NAME}</FormLabel>
              <Tooltip
                title={tooltipComponent(RETAIL_SUB_CATEGORY_TOOLTIPS.name)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            {subCategoryNames.map((name, index) => (
              <Stack
                key={`sub-category-name-${index}`}
                direction="row"
                gap="8px"
                alignItems="flex-start"
              >
                <TextField
                  variant="outlined"
                  size="small"
                  value={name}
                  error={Boolean(nameErrors[index])}
                  helperText={
                    nameErrors[index] ? nameErrorMessages[index] || "" : ""
                  }
                  onChange={(e) => updateNameAt(index, e.target.value)}
                  fullWidth
                  placeholder={
                    selectedId
                      ? undefined
                      : `Sub Category Name ${index + 1}`
                  }
                />
                {!selectedId && (
                  <Stack direction="row" sx={{ pt: "4px" }}>
                    {index === subCategoryNames.length - 1 && (
                      <Tooltip title="Add another name">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={addNameField}
                          aria-label="Add another sub category name"
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {subCategoryNames.length > 1 && (
                      <Tooltip title="Remove name">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeNameField(index)}
                          aria-label="Remove sub category name"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                )}
              </Stack>
            ))}
          </Stack>

          <Stack direction="row" alignItems="center">
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label={RETAIL_SUB_CATEGORY_LABEL_ACTIVE}
            />
            <Tooltip
              title={tooltipComponent(RETAIL_SUB_CATEGORY_TOOLTIPS.active)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Stack>
        </Stack>

        <Stack direction="row" justifyContent="flex-end" mt={2}>
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD}{" "}
            {RETAIL_SUB_CATEGORY_BUTTON_SUFFIX}
          </Button>
        </Stack>

        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            {RETAIL_SUB_CATEGORY_SAMPLE_WARNING}
          </Typography>
          <Typography variant="h6">{RETAIL_SUB_CATEGORY_LIST_TITLE}</Typography>
          {items?.length ? (
            <DraggableItemList
              itemData={dndItemData}
              onOrderUpdate={(order) =>
                setItems(stripRetailSubCategoryDndDisplayFields(order))
              }
              onEdit={editItem}
              onDelete={deleteItemById}
              code={RETAIL_SUB_CATEGORY_DND_LEVEL_LABEL}
              name={RETAIL_SUB_CATEGORY_FIELD_NAME}
            />
          ) : (
            <Typography variant="body2">
              {RETAIL_SUB_CATEGORY_EMPTY_LIST_MESSAGE}
            </Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RetailSubCategories;
